/**
 * Получает размеры изображения из различных источников
 *
 * @description
 * Функция определяет размеры изображения (ширину и высоту) из:
 * - Объекта File
 * - URL ссылки
 * - Base64 строки
 * Использует HTMLImageElement для загрузки изображения и получения его размеров.
 * Автоматически очищает временные URL объекты.
 *
 * @param {File | string} source - Источник изображения:
 *   - File: объект файла изображения
 *   - string: URL ссылка или Base64 строка
 *
 * @returns {Promise<{width: number; height: number}>}
 *   Объект с размерами изображения:
 *   - width: ширина изображения в пикселях
 *   - height: высота изображения в пикселях
 *
 * @throws {Error}
 *   - "ERROR_LOADING_IMAGE" - если не удалось загрузить изображение
 *   - "INVALID_SOURCE" - если передан некорректный источник
 *
 * @example
 * ```typescript
 * // Для File
 * const file = new File(['...'], 'image.jpg', { type: 'image/jpeg' });
 * const size = await getImageSize(file);
 * // { width: 800, height: 600 }
 *
 * // Для URL
 * const size = await getImageSize('https://example.com/image.jpg');
 * // { width: 1024, height: 768 }
 *
 * // Для Base64
 * const size = await getImageSize('data:image/jpeg;base64,...');
 * // { width: 640, height: 480 }
 * ```
 */
export const getImageSize = (
  source: File | string,
): Promise<{ width: number; height: number }> => {
  if (!source) {
    return Promise.reject(new Error("INVALID_SOURCE"));
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl: string | null = null;

    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    img.onload = () => {
      const size = { width: img.width, height: img.height };
      cleanup();
      resolve(size);
    };

    img.onerror = () => {
      cleanup();
      reject(new Error("ERROR_LOADING_IMAGE"));
    };

    try {
      if (source instanceof File) {
        objectUrl = URL.createObjectURL(source);
        img.src = objectUrl;
      } else if (typeof source === "string") {
        if (source.startsWith("data:") || source.startsWith("http")) {
          img.src = source;
        } else {
          throw new Error("INVALID_SOURCE");
        }
      } else {
        throw new Error("INVALID_SOURCE");
      }
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
};
